// Aignoz Founder Templates Library — /templates (Phase 6).
// 4 India-flavored templates · preview + fill placeholders + download PDF/TXT.
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  FileText, ShieldCheck, HandCoins, ScrollText, Award, Loader2, Download, Eye, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const MAGENTA = "#D17EC0";
const API = process.env.REACT_APP_BACKEND_URL + "/api";

const TEMPLATE_META = {
  nda:                   { icon: ShieldCheck, color: "#22D3EE" },
  safe:                  { icon: HandCoins,   color: MAGENTA },
  series_a_term_sheet:   { icon: ScrollText,  color: "#FBBF24" },
  esop_grant_letter:     { icon: Award,       color: "#A78BFA" },
};

// Sensible defaults so the tester (and the demo presenter) doesn't have to type.
const DEFAULT_VALUES = {
  COMPANY_NAME: "Aignoz Healthcare Solutions",
  COMPANY_ADDRESS: "Bengaluru, Karnataka, India",
  FOUNDER_NAME: "Dr. Sriinidhi BV",
  JURISDICTION_CITY: "Bengaluru",
  DATE: new Date().toISOString().slice(0, 10),
  INVESTOR_NAME: "Acme Ventures LLP",
  INVESTOR_SIGNATORY: "Managing Partner",
  COUNTERPARTY_NAME: "Prospect Health Systems",
  COUNTERPARTY_ADDRESS: "Mumbai, Maharashtra, India",
  COUNTERPARTY_SIGNATORY: "Head of Innovation",
  COUNTERPARTY_TITLE: "SVP Corporate Development",
  AMOUNT_INR: "1,00,00,000",
  AMOUNT_USD: "120,000",
  VALUATION_CAP_INR: "20,00,00,000",
  VALUATION_CAP_USD: "2,400,000",
  PRE_MONEY_INR: "16,60,00,000",
  PRE_MONEY_USD: "2,000,000",
  POST_MONEY_INR: "20,00,00,000",
  POST_MONEY_USD: "2,400,000",
  ESOP_PCT: "10",
  LEGAL_CAP_INR: "10,00,000",
  GRANTEE_NAME: "Employee Name",
  GRANTEE_ADDRESS: "Bengaluru, Karnataka, India",
  OPTION_COUNT: "10,000",
  STRIKE_PRICE_INR: "10",
  FACE_VALUE_INR: "10",
  VESTING_START_DATE: new Date().toISOString().slice(0, 10),
};


// ===========================================================================
// PAGE
// ===========================================================================
export default function TemplatesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(null);        // template detail
  const [downloadingId, setDownloadingId] = useState(null);
  const [placeholders, setPlaceholders] = useState({});
  const [format, setFormat] = useState("pdf");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/templates`);
        setItems(data || []);
      } catch (e) {
        toast.error("Could not load templates", { description: e.message });
      } finally { setLoading(false); }
    })();
  }, []);

  async function openPreview(id) {
    try {
      const { data } = await axios.get(`${API}/templates/${id}`);
      setPreviewing(data);
      const seeded = {};
      for (const k of (data.placeholders || [])) seeded[k] = DEFAULT_VALUES[k] || "";
      setPlaceholders(seeded);
      setFormat("pdf");
    } catch (e) {
      toast.error("Could not open preview", { description: e.message });
    }
  }

  async function downloadTemplate() {
    if (!previewing) return;
    setDownloadingId(previewing.id);
    try {
      const res = await axios.post(
        `${API}/templates/${previewing.id}/download`,
        { placeholders, format },
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const ext = format === "docx" ? "txt" : format; // DOCX not installed → server returns TXT
      a.download = `aignoz-${previewing.id.replace(/_/g, "-")}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error("Download failed", { description: e.message });
    } finally { setDownloadingId(null); }
  }

  const filled = useMemo(() => {
    if (!previewing) return "";
    let body = previewing.body || "";
    for (const [k, v] of Object.entries(placeholders || {})) {
      if (v) body = body.replaceAll(`{{${k}}}`, v);
    }
    return body;
  }, [previewing, placeholders]);

  return (
    <div className="h-full overflow-y-auto" data-testid="templates-page">
      <div className="max-w-[1280px] mx-auto px-8 py-10">
        {/* ============================ HEADER ============================ */}
        <header className="mb-8">
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mono mb-1">
            Aignoz Idea Factory · Phase 6
          </div>
          <h1 className="text-4xl font-semibold tracking-tight leading-tight">
            Founder Templates Library
          </h1>
          <p className="text-base text-white/55 italic mt-2 max-w-[64ch] leading-snug">
            Four hand-crafted, India-flavored legal templates every deep-tech founder needs on
            day zero. Fill in the placeholders, download as PDF or TXT, and hand to your lawyer.
          </p>
        </header>

        {/* ============================ CARDS ============================ */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-40 rounded-xl border border-white/[0.06] bg-white/[0.014] animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {items.map((t) => {
              const meta = TEMPLATE_META[t.id] || TEMPLATE_META.nda;
              const Icon = meta.icon;
              return (
                <div
                  key={t.id}
                  data-testid={`templates-card-${t.id}`}
                  className="rounded-xl border border-white/[0.08] bg-white/[0.012] p-5 hover:border-white/[0.18] transition"
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div
                      className="w-10 h-10 rounded-lg border flex items-center justify-center shrink-0"
                      style={{ borderColor: `${meta.color}55`, background: `${meta.color}14` }}
                    >
                      <Icon className="w-5 h-5" style={{ color: meta.color }} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold tracking-tight text-white/95">{t.name}</div>
                      <div className="text-[10px] mono uppercase tracking-[0.14em] text-white/40 mt-0.5">
                        {t.category}
                      </div>
                    </div>
                  </div>
                  <p className="text-[12px] text-white/60 leading-relaxed">{t.description}</p>
                  <div className="mt-3 flex flex-wrap gap-1">
                    {(t.placeholders || []).slice(0, 5).map((p) => (
                      <span key={p} className="text-[9px] px-1.5 py-0.5 rounded border border-white/[0.10] text-white/55 mono uppercase tracking-[0.1em]">
                        {p.toLowerCase().replace(/_/g, " ")}
                      </span>
                    ))}
                    {(t.placeholders || []).length > 5 && (
                      <span className="text-[9px] text-white/40">+{t.placeholders.length - 5} more</span>
                    )}
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button
                      onClick={() => openPreview(t.id)}
                      variant="outline"
                      size="sm"
                      data-testid={`templates-preview-btn-${t.id}`}
                      className="gap-1.5 flex-1"
                    >
                      <Eye className="w-3 h-3" /> Preview
                    </Button>
                    <Button
                      onClick={() => openPreview(t.id)}
                      size="sm"
                      data-testid={`templates-download-btn-${t.id}`}
                      className="gap-1.5 flex-1 text-black"
                      style={{ background: meta.color }}
                    >
                      <Download className="w-3 h-3" /> Download
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ============================ DISCLAIMER ============================ */}
        <div
          data-testid="templates-disclaimer"
          className="mt-10 rounded-md border border-amber-400/25 bg-amber-500/[0.05] p-4 text-[11px] text-amber-100/85 leading-relaxed"
        >
          <strong className="text-amber-200 mono uppercase tracking-[0.14em] text-[10px]">Legal disclaimer:</strong>{" "}
          These templates are for illustrative purposes only. Not legal advice. Consult a qualified
          attorney before use. Aignoz Healthcare Solutions and Dr. Sriinidhi BV assume no liability.
        </div>

        <div className="mt-12 text-center text-[10px] text-white/30">
          Designed by <span className="text-white/55">Dr. Sriinidhi BV (MBBS, MD)</span> · Aignoz Innovation OS™
        </div>
      </div>

      {/* ============================ MODAL ============================ */}
      <Dialog open={!!previewing} onOpenChange={(o) => !o && setPreviewing(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="templates-modal">
          <DialogHeader>
            <DialogTitle>{previewing?.name}</DialogTitle>
            <DialogDescription className="text-[11px] italic">{previewing?.category}</DialogDescription>
          </DialogHeader>
          {previewing && (
            <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-3 min-h-0 flex-1">
              {/* Preview */}
              <div className="rounded-md border border-white/[0.08] bg-black/[0.30] p-3 overflow-y-auto min-h-[300px]">
                <pre className="text-[10.5px] leading-[1.4] whitespace-pre-wrap text-white/85 font-mono">
                  {filled}
                </pre>
              </div>
              {/* Form */}
              <div
                data-testid="templates-modal-form"
                className="rounded-md border border-white/[0.08] bg-white/[0.014] p-3 overflow-y-auto"
              >
                <div className="text-[10px] mono uppercase tracking-[0.14em] text-white/45 mb-3">
                  Fill placeholders
                </div>
                <div className="space-y-2">
                  {(previewing.placeholders || []).map((key) => (
                    <div key={key}>
                      <label className="text-[9px] mono uppercase tracking-[0.1em] text-white/50 block mb-0.5">
                        {key.toLowerCase().replace(/_/g, " ")}
                      </label>
                      <Input
                        data-testid={`templates-placeholder-${key}`}
                        value={placeholders[key] ?? ""}
                        onChange={(e) => setPlaceholders({ ...placeholders, [key]: e.target.value })}
                        className="h-8 text-[11px]"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="items-center pt-3 border-t border-white/[0.06] mt-3">
            <div className="flex items-center gap-2 mr-auto" data-testid="templates-format-picker">
              <span className="text-[10px] mono uppercase tracking-[0.14em] text-white/50">Format</span>
              <Select value={format} onValueChange={setFormat}>
                <SelectTrigger className="w-[130px] h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="txt">TXT</SelectItem>
                  <SelectItem value="docx">DOCX (soon)</SelectItem>
                </SelectContent>
              </Select>
              {format === "docx" && (
                <span className="text-[10px] text-amber-200 mono">Falls back to TXT — python-docx not installed.</span>
              )}
            </div>
            <Button variant="ghost" onClick={() => setPreviewing(null)}>Close</Button>
            <Button
              onClick={downloadTemplate}
              disabled={!!downloadingId}
              data-testid="templates-download-confirm"
              className="gap-2 text-black"
              style={{ background: MAGENTA }}
            >
              {downloadingId ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
